import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CalendarCheck,
  Check,
  ChevronRight,
  Copy,
  CreditCard,
  Gift,
  LockKeyhole,
  Menu,
  MessageCircleHeart,
  QrCode,
  SendHorizontal,
  ShieldCheck,
  Sparkles,
  Star,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { WEDDING_TEMPLATES } from '../lib/weddingTemplates';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import BrandLogo from '../components/BrandLogo';
import SEOHead from '../components/SEOHead';
import { getMarketingSeoConfig } from '../lib/seo';
import { getMarketingPageContent } from '../lib/marketingContent';
import SupplierCard from '../components/SupplierCard';
import { SUPPLIERS_BETA_ENABLED } from '../lib/featureFlags';
import { normalizeObjectStrings } from '../lib/utils';

const TEMPLATE_PREVIEW_FALLBACK = 'https://images.unsplash.com/photo-1519741497674-611481863552?w=800';
const LANDING_HERO_IMAGE = 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=2200&q=80';
const TESTIMONIALS = [
  // TODO: trocar por depoimentos reais validados pelo time comercial.
  {
    quote: 'Conseguimos publicar rápido e compartilhar com todo mundo sem complicação.',
    author: 'Marina e Thiago',
    highlight: 'Publicação em poucos minutos',
  },
  {
    quote: 'A lista ficou linda, fácil de usar e os convidados conseguiram presentear sem dificuldade.',
    author: 'Camila e Rodrigo',
    highlight: 'Experiência elogiada pelos convidados',
  },
  {
    quote: 'O painel ajudou muito a acompanhar confirmações e presentes em um só lugar.',
    author: 'Fernanda e Lucas',
    highlight: 'Controle em tempo real',
  },
];

const HOW_IT_WORKS_ICONS = [Sparkles, Gift, SendHorizontal, CalendarCheck];

const BENEFIT_ICONS = [Users, Wallet, QrCode, MessageCircleHeart];

const TRUST_ITEMS = [
  {
    title: 'Dados protegidos',
    description: 'Camadas de segurança para proteger suas informações e transações ao longo da jornada.',
    icon: LockKeyhole,
  },
  {
    title: 'Fluxo monitorado',
    description: 'Acompanhe eventos de pagamento e status importantes com mais clareza e previsibilidade.',
    icon: ShieldCheck,
  },
  {
    title: 'Experiência pensada para converter',
    description: 'Design simples, elegante e focado em facilitar a decisão dos seus convidados.',
    icon: Star,
  },
];

const TRUST_FACTS = [
  {
    title: 'Templates prontos para publicar',
    description: 'Escolha um estilo elegante e personalize seu site com mais rapidez.',
  },
  {
    title: 'Plano único sem assinatura',
    description: 'Você publica seu site sem custo de adesão e paga apenas 3,59% por presente recebido.',
  },
  {
    title: 'Taxa clara desde o início',
    description: 'Transparência para você saber exatamente quanto entra na carteira antes do saque.',
  },
];

const PLANS = [
  {
    id: 'gratuito',
    name: 'Plano Grátis',
    price: 'R$ 0',
    billing: 'sem assinatura',
    methods: ['PIX', 'Cartão'],
    highlight: true,
    description: 'Publique seu site e receba presentes com taxa fixa de 3,59% por presente confirmado.',
    features: [
      'Site personalizado do casal',
      'Lista de presentes ilimitada',
      'Confirmação de presença e gestão de convidados',
      'Checkout para convidados com PIX e cartão',
      'Painel completo para acompanhar recebimentos',
      'Taxa da plataforma: 3,59% por presente recebido',
    ],
    cta: 'Começar agora',
    badge: 'Menor taxa do Brasil',
  },
];

const BASE_NAV_ITEMS = [
  { href: '#templates', label: 'Templates' },
  { href: '#como-funciona', label: 'Como funciona' },
  { href: '#planos', label: 'Taxas e condições' },
  { href: '#faq', label: 'FAQ' },
];

const COUPON_CODE = 'VIREINOIVA10';
const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);
const API = `${BACKEND_URL}/api`;

export default function LandingPage({ seoKey = 'home', showCouponBanner = false }) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const spotlightTemplates = useMemo(() => WEDDING_TEMPLATES.slice(0, 4), []);
  const seoConfig = useMemo(() => getMarketingSeoConfig(seoKey), [seoKey]);
  const pageContent = useMemo(() => getMarketingPageContent(seoKey), [seoKey]);
  const navItems = useMemo(() => (
    SUPPLIERS_BETA_ENABLED
      ? [...BASE_NAV_ITEMS, { href: '/fornecedores-beta', label: 'Fornecedores', isRoute: true }]
      : BASE_NAV_ITEMS
  ), []);

  const [guestSlug, setGuestSlug] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [featuredSuppliers, setFeaturedSuppliers] = useState([]);
  const [couponBannerDismissed, setCouponBannerDismissed] = useState(false);
  const primaryHref = isAuthenticated ? '/dashboard' : '/register';
  const loginHref = isAuthenticated ? '/dashboard' : '/login';
  const primaryButtonLabel = isAuthenticated ? 'Meu painel' : 'Criar meu site';
  const shouldShowCouponBanner = showCouponBanner && !couponBannerDismissed;
  const mainPaddingClass = shouldShowCouponBanner ? 'pb-36 md:pb-32' : 'pb-24 md:pb-0';
  const buildTemplatePreviewPath = (templateId) => `/s/exemplo?template=${encodeURIComponent(templateId)}`;

  useEffect(() => {
    if (!SUPPLIERS_BETA_ENABLED) return undefined;

    let active = true;
    axios.get(`${API}/suppliers/public?limit=6`)
      .then((response) => {
        if (active) {
          const items = normalizeObjectStrings(response.data || []);
          setFeaturedSuppliers(items);
        }
      })
      .catch(() => {
        if (active) setFeaturedSuppliers([]);
      });

    return () => {
      active = false;
    };
  }, []);

  const handleTemplatePreviewError = (event) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = TEMPLATE_PREVIEW_FALLBACK;
  };

  const handleGuestAccess = (event) => {
    event.preventDefault();
    const slug = guestSlug.trim();

    if (!slug) {
      toast.error('Digite o código ou nome da lista para continuar.');
      return;
    }

    navigate(`/s/${encodeURIComponent(slug)}`);
  };

  const handleTemplateSelect = (templateId) => {
    const safeTemplateId = String(templateId || '').trim();
    if (!safeTemplateId) return;

    sessionStorage.setItem('selectedTemplateId', safeTemplateId);

    if (!isAuthenticated) {
      navigate(buildTemplatePreviewPath(safeTemplateId));
      return;
    }

    navigate(`/editor?template=${encodeURIComponent(safeTemplateId)}`);
  };

  const handleCouponCopy = async () => {
    try {
      await navigator.clipboard.writeText(COUPON_CODE);
      toast.success(`Cupom ${COUPON_CODE} copiado.`);
    } catch (error) {
      toast.error('Não conseguimos copiar o cupom automaticamente.');
    }
  };

  const renderNavItem = (item, extraProps = {}) => {
    if (item.isRoute) {
      return (
        <Link
          key={item.href}
          to={item.href}
          {...extraProps}
          className={`rounded-full px-4 py-2 transition-all hover:bg-[#E8B7B1]/15 hover:text-[#1C1C1C] ${extraProps.className || ''}`.trim()}
        >
          {item.label}
        </Link>
      );
    }

    return (
      <a
        key={item.href}
        href={item.href}
        {...extraProps}
        className={`rounded-full px-4 py-2 transition-all hover:bg-[#E8B7B1]/15 hover:text-[#1C1C1C] ${extraProps.className || ''}`.trim()}
      >
        {item.label}
      </a>
    );
  };

  return (
    <div className="min-h-screen bg-[#F6F1EF] text-[#1C1C1C]">
      <SEOHead
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={seoConfig.canonical}
        jsonLd={seoConfig.jsonLd}
      />
      <header className="sticky top-0 z-50 border-b border-[#E8B7B1]/35 bg-[#F6F1EF]/90 backdrop-blur-md">
        <div className="container-wedding flex h-[5.25rem] items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center transition-opacity hover:opacity-95"
            data-testid="brand-link"
          >
            <BrandLogo size="lg" showTagline />
          </Link>

          <nav className="hidden items-center gap-1 rounded-full border border-[#E8B7B1]/35 bg-white/65 px-2 py-1 text-sm font-medium text-[#5A4C49] lg:flex">
            {navItems.map((item) => renderNavItem(item))}
          </nav>

          <div className="hidden items-center gap-3 sm:flex">
            {!isAuthenticated && (
              <Link to={loginHref}>
                <Button
                  variant="outline"
                  className="h-11 rounded-full border-[#E8B7B1] bg-white px-6 text-sm text-[#E8B7B1] hover:bg-[#F6F1EF]"
                  data-testid="header-login-btn"
                >
                  Entrar
                </Button>
              </Link>
            )}
            <Link to={primaryHref}>
              <Button
                className="h-11 rounded-full bg-[#E8B7B1] px-6 text-sm font-semibold text-white hover:bg-[#DDA39D]"
                data-testid="header-primary-btn"
              >
                {primaryButtonLabel}
              </Button>
            </Link>
          </div>

          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#E8B7B1]/55 bg-white/80 text-[#A88574] sm:hidden"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="border-t border-[#E8B7B1]/30 bg-[#F6F1EF]/95 sm:hidden">
            <div className="container-wedding py-4">
              <nav className="mb-4 grid gap-2 rounded-2xl border border-[#E8B7B1]/35 bg-white/70 p-2 text-sm font-medium text-[#5A4C49]">
                {navItems.map((item) => renderNavItem(item, {
                  onClick: () => setMobileMenuOpen(false),
                  className: 'rounded-xl px-3 py-2',
                }))}
              </nav>

              <div className="grid gap-2">
                {!isAuthenticated && (
                  <Link to={loginHref} onClick={() => setMobileMenuOpen(false)}>
                    <Button
                      variant="outline"
                      className="h-11 w-full rounded-full border-[#E8B7B1] bg-white text-[#E8B7B1] hover:bg-[#F6F1EF]"
                      data-testid="header-login-btn-mobile"
                    >
                      Entrar
                    </Button>
                  </Link>
                )}
                <Link to={primaryHref} onClick={() => setMobileMenuOpen(false)}>
                  <Button
                    className="h-11 w-full rounded-full bg-[#E8B7B1] text-sm font-semibold text-white hover:bg-[#DDA39D]"
                    data-testid="header-primary-btn-mobile"
                  >
                    {primaryButtonLabel}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className={mainPaddingClass}>
        <section className="relative isolate overflow-hidden border-b border-[#E8B7B1]/30">
          <img
            src={LANDING_HERO_IMAGE}
            alt="Casal em ensaio de casamento"
            className="hero-image-motion absolute inset-0 h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F6F1EF]/95 via-[#F6F1EF]/80 to-[#F6F1EF]/46 md:to-[#F6F1EF]/26" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_78%_14%,rgba(255,255,255,0.5),transparent_48%)]" />
          <div className="hero-edge-fade absolute inset-0" />

          <div className="container-wedding relative flex min-h-[calc(100vh-80px)] items-center py-14 md:py-18 lg:py-20 xl:py-24">
            <div className="max-w-3xl rounded-[1.6rem] border border-white/65 bg-[#F6F1EF]/72 p-7 shadow-[0_30px_70px_-35px_rgba(28,28,28,0.48)] backdrop-blur-md md:p-10 xl:max-w-[51rem] xl:p-12 fade-in-up">
              <p className="hero-badge-glow mb-3 inline-flex rounded-full border border-[#E8B7B1]/45 bg-white/90 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#C8A97E]">
                {pageContent.heroBadge}
              </p>

              <h1 className="mb-4 text-4xl leading-tight md:text-6xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                {pageContent.heroTitle}
              </h1>

              <p className="mb-8 max-w-2xl text-lg leading-relaxed text-[#333333]">
                {pageContent.heroDescription}
              </p>

              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Link to={primaryHref}>
                  <Button className="h-12 rounded-lg bg-[#E8B7B1] px-7 text-base font-semibold text-white hover:bg-[#DDA39D]" data-testid="hero-primary-cta">
                    Criar meu site agora
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a href="#como-funciona">
                  <Button variant="outline" className="h-12 rounded-lg border-[#E8B7B1] bg-white/90 px-7 text-base text-[#E8B7B1] hover:bg-[#F6F1EF]">
                    Ver como funciona
                  </Button>
                </a>
              </div>

              <div className="mt-7 grid gap-3 sm:grid-cols-3">
                {pageContent.heroCards.map((item) => (
                  <div key={item.title} className="rounded-xl border border-[#E8B7B1]/45 bg-white/80 px-3 py-3">
                    {item.eyebrow ? (
                      <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#C8A97E]">{item.eyebrow}</p>
                    ) : null}
                    <p className="text-xl font-semibold text-[#1C1C1C]">{item.title}</p>
                    <p className="text-xs text-[#5A4C49]">{item.description}</p>
                  </div>
                ))}
              </div>

              <article className="hero-side-card-sway mt-6 rounded-2xl border border-white/60 bg-[#F6F1EF]/56 p-5 shadow-[0_22px_44px_-30px_rgba(28,28,28,0.5)] backdrop-blur-xl xl:hidden">
                <h3 className="text-[1.85rem] leading-[1.06] text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Se você está perdida com tanta coisa do casamento... você não está sozinha.
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#3F3A38]">
                  Lista de convidados, presentes, confirmações e organização. Tudo parece simples até começar.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-[#3F3A38]">
                  E é exatamente por isso que criamos o Virei Noiva.
                </p>
                <div className="my-4 h-px w-full bg-[#E8B7B1]/55" />
                <h4 className="text-[1.8rem] leading-[1.08] text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Tudo que você precisa, em um só lugar
                </h4>
                <p className="mt-3 text-sm leading-relaxed text-[#3F3A38]">
                  Seus convidados escolhem o presente...
                </p>
                <p className="mt-2 text-sm leading-relaxed text-[#3F3A38]">
                  E você recebe o valor direto, sem dor de cabeça.
                </p>
              </article>
            </div>

            <div className="pointer-events-none absolute right-3 top-1/2 hidden w-[22rem] -translate-y-1/2 xl:block 2xl:right-6">
              <article className="hero-side-card-sway rounded-[1.55rem] border border-white/60 bg-[#F6F1EF]/56 p-6 shadow-[0_24px_44px_-30px_rgba(28,28,28,0.55)] backdrop-blur-xl">
                <h3 className="text-[1.95rem] leading-[1.06] text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Se você está perdida com tanta coisa do casamento... você não está sozinha.
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#3F3A38]">
                  Lista de convidados, presentes, confirmações e organização. Tudo parece simples até começar.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-[#3F3A38]">
                  E é exatamente por isso que criamos o Virei Noiva.
                </p>
                <div className="my-4 h-px w-full bg-[#E8B7B1]/55" />
                <h4 className="text-[1.9rem] leading-[1.08] text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Tudo que você precisa, em um só lugar
                </h4>
                <p className="mt-3 text-sm leading-relaxed text-[#3F3A38]">
                  Seus convidados escolhem o presente...
                </p>
                <p className="mt-2 text-sm leading-relaxed text-[#3F3A38]">
                  E você recebe o valor direto, sem dor de cabeça.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="container-wedding py-12" id="acessos">
          <div className={`grid gap-5 ${SUPPLIERS_BETA_ENABLED ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
            <article className="rounded-2xl border border-[#E8B7B1]/40 bg-white p-6 shadow-[0_18px_32px_-24px_rgba(28,28,28,0.35)]">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C8A97E]">Sou o casal</p>
              <h2 className="mt-2 text-2xl md:text-3xl" style={{ fontFamily: 'Playfair Display, serif' }}>Quero criar meu site e lista</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#333333]">
                Monte sua página, publique sua lista e compartilhe com os convidados em poucos passos.
              </p>
              <Link to={primaryHref} className="mt-5 inline-flex">
                <Button className="h-11 rounded-lg bg-[#E8B7B1] px-6 text-white hover:bg-[#DDA39D]">
                  Criar meu site agora
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </article>

            <article className="rounded-2xl border border-[#E8B7B1]/40 bg-white p-6 shadow-[0_18px_32px_-24px_rgba(28,28,28,0.35)]">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C8A97E]">Sou convidado</p>
              <h2 className="mt-2 text-2xl md:text-3xl" style={{ fontFamily: 'Playfair Display, serif' }}>Sou convidado e quero acessar uma lista</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#333333]">
                Digite o código ou link recebido para visualizar os presentes e presentear em segundos.
              </p>
              <form onSubmit={handleGuestAccess} className="mt-5 flex flex-col gap-3 sm:flex-row">
                <input
                  type="text"
                  value={guestSlug}
                  onChange={(event) => setGuestSlug(event.target.value)}
                  placeholder="Digite o código ou nome da lista"
                  className="input-wedding flex-1"
                  data-testid="guest-slug-input"
                />
                <Button type="submit" className="h-11 rounded-lg bg-[#E8B7B1] px-6 text-white hover:bg-[#DDA39D]" data-testid="guest-access-btn">
                  Acessar lista
                </Button>
              </form>
            </article>

            {SUPPLIERS_BETA_ENABLED ? (
              <article className="rounded-2xl border border-[#E8B7B1]/40 bg-white p-6 shadow-[0_18px_32px_-24px_rgba(28,28,28,0.35)]">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C8A97E]">Trabalha com casamentos?</p>
                <h2 className="mt-2 text-2xl md:text-3xl" style={{ fontFamily: 'Playfair Display, serif' }}>Crie seu perfil de fornecedor</h2>
                <p className="mt-3 text-sm leading-relaxed text-[#333333]">
                  Entre na vitrine de fornecedores, monte seu perfil profissional e apareca para casais dentro do Virei Noiva.
                </p>
                <div className="mt-5 flex flex-col gap-3">
                  <Link to="/fornecedores/anuncie" className="inline-flex">
                    <Button className="h-11 rounded-lg bg-[#1C1C1C] px-6 text-white hover:bg-[#333333]">
                      Conhecer a area de fornecedores
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/fornecedor/login" className="inline-flex text-sm font-medium text-[#B97D5B]">
                    Ja tenho conta
                  </Link>
                </div>
              </article>
            ) : null}
          </div>
        </section>

        <section className="border-y border-[#E8B7B1]/30 bg-white py-12">
          <div className="container-wedding">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                {pageContent.keywordSectionTitle}
              </h2>
              <p className="mt-3 text-[#333333]">
                {pageContent.keywordSectionDescription}
              </p>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {pageContent.relatedRoutes.map((route) => (
                <Link
                  key={route.key}
                  to={route.href}
                  className="rounded-2xl border border-[#E8B7B1]/35 bg-[#F6F1EF] p-5 shadow-[0_12px_26px_-24px_rgba(28,28,28,0.32)] transition hover:-translate-y-0.5"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C8A97E]">Página relacionada</p>
                  <h3 className="mt-2 text-2xl text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {route.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#333333]">
                    {route.description}
                  </p>
                  <p className="mt-4 inline-flex items-center text-sm font-medium text-[#B97D5B]">
                    Ver página
                    <ArrowRight className="ml-1.5 h-4 w-4" />
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-[#E8B7B1]/30 bg-white py-14">
          <div className="container-wedding">
            <h2 className="text-center text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>
              {pageContent.socialTitle}
            </h2>
            <p className="mx-auto mt-3 max-w-3xl text-center text-[#333333]">
              {pageContent.socialDescription}
            </p>
            <div className="mt-9 grid gap-4 md:grid-cols-3">
              {TESTIMONIALS.map((item) => (
                <article key={item.author} className="rounded-2xl border border-[#E8B7B1]/35 bg-[#F6F1EF] p-5 shadow-[0_12px_26px_-24px_rgba(28,28,28,0.3)]">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C8A97E]">{item.highlight}</p>
                  <p className="mt-3 text-sm leading-relaxed text-[#333333]">"{item.quote}"</p>
                  <p className="mt-4 text-base font-semibold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {item.author}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {SUPPLIERS_BETA_ENABLED ? (
          <section className="container-wedding py-14" id="fornecedores">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="max-w-3xl">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#C8A97E]">Vitrine de fornecedores</p>
                <h2 className="mt-2 text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Entre na vitrine do Virei Noiva
                </h2>
                <p className="mt-3 text-[#333333]">
                  Abra seu perfil, ganhe visibilidade e apareca para casais que ja estao organizando o casamento dentro da plataforma.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Link to="/fornecedores">
                  <Button variant="outline" className="h-11 rounded-full border-[#E8B7B1] bg-white px-6 text-[#9A6B65] hover:bg-[#F6F1EF]">
                    Conhecer a vitrine
                  </Button>
                </Link>
                <Link to="/fornecedores/anuncie">
                  <Button className="h-11 rounded-full bg-[#1C1C1C] px-6 text-white hover:bg-[#333333]">
                    Quero anunciar meu negocio
                  </Button>
                </Link>
              </div>
            </div>

            {featuredSuppliers.length > 0 ? (
              <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                {featuredSuppliers.map((supplier) => (
                  <SupplierCard key={supplier.supplier_id} supplier={supplier} compact />
                ))}
              </div>
            ) : (
              <div className="mt-8 grid gap-5 lg:grid-cols-[1.15fr_0.85fr]">
                <article className="rounded-[2rem] border border-[#E8B7B1]/35 bg-white p-7 shadow-[0_20px_50px_-30px_rgba(28,28,28,0.24)]">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C8A97E]">Primeiras vagas abertas</p>
                  <h3 className="mt-3 text-3xl text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Seja um dos primeiros fornecedores na vitrine do Virei Noiva
                  </h3>
                  <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#4B403D]">
                    Cadastre seu negocio agora para ganhar presenca antecipada, validar sua marca dentro da plataforma e conquistar os espacos de maior visibilidade da vitrine.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Link to="/fornecedores/anuncie">
                      <Button className="h-11 rounded-full bg-[#1C1C1C] px-6 text-white hover:bg-[#333333]">
                        Quero aparecer no Virei Noiva
                      </Button>
                    </Link>
                    <Link to="/fornecedor/cadastro">
                      <Button variant="outline" className="h-11 rounded-full border-[#E8B7B1] bg-white px-6 text-[#9A6B65] hover:bg-[#F6F1EF]">
                        Criar meu perfil
                      </Button>
                    </Link>
                  </div>
                </article>

                <article className="rounded-[2rem] border border-[#E8B7B1]/35 bg-[#FDF6F3] p-7 shadow-[0_20px_50px_-30px_rgba(28,28,28,0.2)]">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C8A97E]">Por que entrar cedo</p>
                  <div className="mt-4 space-y-4 text-sm leading-relaxed text-[#4B403D]">
                    <p>Ganhe visibilidade antes da entrada de novos anunciantes.</p>
                    <p>Teste sua presenca na plataforma com menos concorrencia.</p>
                    <p>Posicione sua marca para futuras areas premium e destaques na home.</p>
                  </div>
                </article>
              </div>
            )}
          </section>
        ) : null}

        <section className="container-wedding py-14" id="templates">
          <h2 className="text-center text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>
            {pageContent.templatesTitle}
          </h2>
          <p className="mt-3 text-center text-[#333333]">
            {pageContent.templatesDescription}
          </p>
          <div className="mt-6 flex justify-center">
            <Link to={primaryHref}>
              <Button variant="outline" className="h-11 rounded-lg border-[#E8B7B1] bg-white px-6 text-[#E8B7B1] hover:bg-[#F6F1EF]">
                Ver todos os templates
              </Button>
            </Link>
          </div>

          <div className="mt-9 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {spotlightTemplates.map((template) => (
              <button
                type="button"
                key={template.id}
                onClick={() => handleTemplateSelect(template.id)}
                className="group overflow-hidden rounded-2xl border border-[#E8B7B1]/35 bg-white text-left shadow-[0_12px_22px_-24px_rgba(28,28,28,0.35)] transition hover:-translate-y-1"
                data-testid={`template-${template.id}`}
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={template.preview_image}
                    alt={template.label || template.name}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    loading="lazy"
                    onError={handleTemplatePreviewError}
                  />
                </div>
                <div className="border-t border-[#E8B7B1]/35 px-4 py-4">
                  <h3 className="text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {template.label || template.name}
                  </h3>
                  <p className="mt-1 text-xs uppercase tracking-[0.16em] text-[#C8A97E]">{template.collection}</p>
                  <p className="mt-3 inline-flex items-center text-sm font-medium text-[#B97D5B]">
                    {isAuthenticated ? 'Editar este template' : 'Ver prévia deste template'}
                    <ArrowRight className="ml-1.5 h-4 w-4" />
                  </p>
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="border-y border-[#E8B7B1]/30 bg-white py-14" id="como-funciona">
          <div className="container-wedding">
            <h2 className="text-center text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>
              {pageContent.howItWorksTitle}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-[#333333]">
              {pageContent.howItWorksDescription}
            </p>

            <div className="mt-9 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {pageContent.howItWorks.map((step, index) => {
                const Icon = HOW_IT_WORKS_ICONS[index] || Sparkles;
                return (
                  <article key={step.title} className="rounded-2xl border border-[#E8B7B1]/35 bg-[#F6F1EF] p-5 shadow-[0_12px_26px_-24px_rgba(28,28,28,0.38)] transition hover:-translate-y-1">
                    <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#C8A97E]">
                      <Icon className="h-5 w-5" />
                    </div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E]">Passo {index + 1}</p>
                    <h3 className="mt-1 text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>{step.title}</h3>
                    <p className="mt-2 text-sm text-[#333333]">{step.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="container-wedding py-14">
          <h2 className="text-center text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>
            {pageContent.benefitsTitle}
          </h2>
          <p className="mx-auto mt-3 max-w-3xl text-center text-[#333333]">
            {pageContent.benefitsDescription}
          </p>
          <div className="mt-9 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {pageContent.benefits.map((benefit, index) => {
              const Icon = BENEFIT_ICONS[index] || Sparkles;
              return (
                <article key={benefit.title} className="rounded-2xl border border-[#E8B7B1]/35 bg-white p-5 shadow-[0_12px_26px_-24px_rgba(28,28,28,0.35)] transition hover:-translate-y-0.5">
                  <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#E8B7B1]/20 text-[#C8A97E]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {benefit.title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-[#333333]">{benefit.description}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="border-y border-[#E8B7B1]/30 bg-white py-14" id="planos">
          <div className="container-wedding">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                Plano único, com taxa clara
              </h2>
              <p className="mt-3 text-[#333333]">
                Comece grátis e publique seu site com transparência total: 3,59% por presente confirmado.
              </p>
            </div>

            <div className="mx-auto mt-8 grid max-w-3xl gap-6">
              {PLANS.map((plan) => (
                <article
                  key={plan.id}
                  className={`rounded-2xl border p-6 shadow-[0_14px_30px_-24px_rgba(28,28,28,0.35)] transition hover:-translate-y-1 ${
                    plan.highlight
                      ? 'border-[#E8B7B1] bg-[#E8B7B1]/15'
                      : 'border-[#E8B7B1]/35 bg-white'
                  }`}
                >
                  {plan.badge ? (
                    <p className="mb-2 inline-flex rounded-full bg-[#C8A97E] px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white">
                      {plan.badge}
                    </p>
                  ) : null}

                  <h3 className="text-3xl" style={{ fontFamily: 'Playfair Display, serif' }}>{plan.name}</h3>
                  <p className="mt-2 text-sm text-[#333333]">{plan.description}</p>

                  <div className="mt-5">
                    <p className="text-4xl font-semibold text-[#1C1C1C]">{plan.price}</p>
                    <p className="text-sm text-[#5A4C49]">{plan.billing}</p>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {plan.methods.map((method) => (
                      <span key={method} className="inline-flex items-center rounded-full border border-[#E8B7B1]/45 bg-white px-3 py-1 text-xs font-semibold text-[#5A4C49]">
                        {method === 'Cartão' ? <CreditCard className="mr-1 h-3.5 w-3.5" /> : <Wallet className="mr-1 h-3.5 w-3.5" />}
                        {method}
                      </span>
                    ))}
                  </div>

                  <Accordion type="single" collapsible className="mt-4">
                    <AccordionItem value={`detalhes-${plan.id}`} className="border-[#E8B7B1]/25">
                      <AccordionTrigger className="py-2 text-sm text-[#5A4C49] hover:no-underline">
                        Ver o que está incluído
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="space-y-2 text-sm text-[#333333]">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex items-start gap-2">
                              <Check className="mt-0.5 h-4 w-4 text-[#C8A97E]" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  <div className="mt-6">
                    <Link to={primaryHref}>
                      <Button className="h-11 w-full rounded-lg bg-[#E8B7B1] text-white hover:bg-[#DDA39D]">
                        {plan.cta}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            <p className="mt-6 text-center text-sm text-[#5A4C49]">
              Sem assinatura mensal: você publica grátis e paga apenas 3,59% por presente confirmado.
            </p>
          </div>
        </section>

        <section className="container-wedding py-14">
          <h2 className="text-center text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>
            Confiança para o seu grande dia
          </h2>
          <p className="mx-auto mt-3 max-w-3xl text-center text-[#333333]">
            Segurança, transparência e acompanhamento para você publicar com mais tranquilidade.
          </p>

          <div className="mt-9 grid gap-4 md:grid-cols-3">
            {TRUST_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.title} className="rounded-2xl border border-[#E8B7B1]/35 bg-white p-5 shadow-[0_12px_26px_-24px_rgba(28,28,28,0.35)]">
                  <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#E8B7B1]/20 text-[#C8A97E]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>{item.title}</h3>
                  <p className="mt-2 text-sm text-[#333333]">{item.description}</p>
                </article>
              );
            })}
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {TRUST_FACTS.map((fact) => (
              <article key={fact.title} className="rounded-2xl border border-[#E8B7B1]/35 bg-[#F6F1EF] p-5 text-sm text-[#333333]">
                <p className="font-semibold text-[#1C1C1C]">{fact.title}</p>
                <p className="mt-2">{fact.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="container-wedding pb-14 text-center">
          <div className="rounded-[1.4rem] border border-[#E8B7B1]/35 bg-white px-8 py-10 shadow-[0_18px_30px_-24px_rgba(28,28,28,0.28)]">
            <h2 className="text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>
              {pageContent.ctaTitle}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-[#333333]">
              {pageContent.ctaDescription}
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link to={primaryHref}>
                <Button className="h-12 rounded-lg bg-[#E8B7B1] px-8 text-base text-white hover:bg-[#DDA39D]">
                  Criar meu site agora
                </Button>
              </Link>
              <a href="#planos">
                <Button variant="outline" className="h-12 rounded-lg border-[#E8B7B1] bg-white px-8 text-base text-[#E8B7B1] hover:bg-[#F6F1EF]">
                  Ver taxa de 3,59%
                </Button>
              </a>
            </div>
          </div>
        </section>

        <section className="border-y border-[#E8B7B1]/30 bg-white py-14" id="faq">
          <div className="container-wedding">
            <h2 className="text-center text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>
              Perguntas frequentes
            </h2>

            <div className="mx-auto mt-8 max-w-3xl rounded-2xl border border-[#E8B7B1]/30 bg-[#F6F1EF] px-5 py-2">
              <Accordion type="single" collapsible>
                {pageContent.faqItems.map((faq) => (
                  <AccordionItem key={faq.id} value={faq.id} className="border-[#E8B7B1]/30">
                    <AccordionTrigger className="text-base text-[#1C1C1C] hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-[#333333]">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#E8B7B1]/30 bg-[#F6F1EF] py-8">
        <div className="container-wedding flex flex-col items-center justify-between gap-4 md:flex-row">
          <BrandLogo size="sm" />
          <p className="text-sm text-[#333333]">© {currentYear} Virei Noiva. Todos os direitos reservados.</p>
        </div>
      </footer>

      {shouldShowCouponBanner ? (
        <div className="fixed bottom-4 left-0 z-40 w-full px-4">
          <div className="relative mx-auto flex max-w-5xl flex-col gap-3 rounded-[1.75rem] border border-[#E8B7B1]/55 bg-[#fff8f5]/95 px-4 py-4 shadow-[0_18px_42px_-18px_rgba(28,28,28,0.38)] backdrop-blur-md md:flex-row md:items-center md:justify-between md:px-6">
            <button
              type="button"
              onClick={() => setCouponBannerDismissed(true)}
              className="absolute right-5 top-5 inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#E8B7B1]/35 bg-white/80 text-[#9E6F62] transition-colors hover:bg-white hover:text-[#1C1C1C]"
              aria-label="Fechar cupom"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-3">
              <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#E8B7B1] text-white">
                <Gift className="h-5 w-5" />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#C8A97E]">Cupom de boas-vindas</p>
                <p className="mt-1 text-sm text-[#5A4C49] md:text-base">
                  Use o cupom e reduza a taxa da plataforma de <span className="font-semibold text-[#1C1C1C]">3,59%</span> para <span className="font-semibold text-[#1C1C1C]">2,59%</span>.
                </p>
                <div className="mt-2 inline-flex items-center rounded-2xl border border-[#E8B7B1]/55 bg-white px-3 py-2 shadow-[0_10px_24px_-20px_rgba(28,28,28,0.4)]">
                  <span className="mr-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#C8A97E]">Use o cupom</span>
                  <span className="rounded-full bg-[#F7E1DC] px-3 py-1 text-sm font-semibold tracking-[0.18em] text-[#9E6F62]">
                    {COUPON_CODE}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                onClick={handleCouponCopy}
                className="h-11 rounded-full border-[#1C1C1C]/15 bg-white px-5 text-[#1C1C1C] hover:bg-[#F6F1EF]"
              >
                <Copy className="mr-2 h-4 w-4" />
                Copiar cupom
              </Button>
              <Link to={primaryHref}>
                <Button className="h-11 w-full rounded-full bg-[#E8B7B1] px-6 text-white hover:bg-[#DDA39D] sm:w-auto">
                  Aplicar desconto
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="fixed bottom-4 left-0 z-40 w-full px-4 md:hidden">
          <div className="mx-auto flex max-w-md gap-2 rounded-2xl border border-[#E8B7B1]/50 bg-white/95 p-2 shadow-[0_12px_24px_-14px_rgba(28,28,28,0.38)] backdrop-blur-md">
            <Link to={primaryHref} className="flex-1">
              <Button className="h-11 w-full rounded-lg bg-[#E8B7B1] text-white hover:bg-[#DDA39D]">Começar agora</Button>
            </Link>
            <a href="#planos" className="flex-1">
              <Button variant="outline" className="h-11 w-full rounded-lg border-[#E8B7B1] text-[#E8B7B1]">Ver taxa</Button>
            </a>
          </div>
        </div>
      )}
    </div>
  );
}



